import { useCallback, useEffect, useRef, useState } from "react";

import { getPublicationState, publishTerritory, PublicationApiError, type PublicationState } from "../../lib/publication-api";

type Status = "idle" | "loading" | "ready" | "error";

/**
 * Owner-facing publication state + Publish/Republish action.
 *
 * Stale-safe: a monotonic request id + AbortController drop any response that
 * completes after the identity changed / component unmounted, so a slow prior
 * request can never overwrite current authority (mirrors useTerritoryIdentity).
 */
export function usePublication(identityId: string | null) {
  const [state, setState] = useState<PublicationState | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [actionError, setActionError] = useState<{ code: string; message: string } | null>(null);
  const [publishing, setPublishing] = useState(false);
  const reqRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!identityId) return;
    const id = ++reqRef.current;
    const controller = new AbortController();
    setStatus((s) => (s === "ready" ? s : "loading"));
    try {
      const st = await getPublicationState(identityId, controller.signal);
      if (id !== reqRef.current) return;
      setState(st);
      setStatus("ready");
    } catch (e) {
      if (id !== reqRef.current) return;
      if ((e as { name?: string }).name === "AbortError") return;
      setStatus("error");
    }
  }, [identityId]);

  useEffect(() => {
    reqRef.current++;
    setState(null);
    setActionError(null);
    if (identityId) void refresh();
    return () => { reqRef.current++; };
  }, [identityId, refresh]);

  const publish = useCallback(
    async (expected: { expected_draft_revision: number; expected_identity_revision: number; expected_membership_fingerprint: string }) => {
      if (!identityId) return;
      const id = ++reqRef.current;
      setPublishing(true);
      setActionError(null);
      try {
        const st = await publishTerritory(identityId, expected);
        if (id !== reqRef.current) return;
        setState(st);
        setStatus("ready");
      } catch (e) {
        if (id !== reqRef.current) return;
        const err = e as PublicationApiError;
        setActionError({ code: err.code ?? "PUBLICATION_ERROR", message: err.message });
        // Re-sync authoritative state so the UI reflects backend truth after a reject.
        void refresh();
      } finally {
        if (id === reqRef.current) setPublishing(false);
      }
    },
    [identityId, refresh],
  );

  return { state, status, actionError, publishing, publish, refresh };
}
